import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild } from "@angular/core";
import { ControlContainer, FormControl, FormGroup } from "@angular/forms";
import { CheckboxComponent } from "@tylertech/forge";
import { getLoggerFor } from "@tylertech/logging";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { LgdFormControl, LgdFormControlBaseConfig } from "../lgdCommon";

@Component({
    selector: "lgd-checkbox",
    templateUrl: "./lgdCheckbox.tmpl.html",
    styleUrls: ["./lgdCheckbox.component.scss"],
    standalone: false
})
export class LgdCheckboxComponent implements AfterViewInit, OnDestroy
{
    private readonly destructor$ = new Subject<void>();
    private readonly log = getLoggerFor("LgdCheckboxComponent");

    @ViewChild("input") readonly inputElement: ElementRef<CheckboxComponent>;

    @Input() readonly controlName: string | number;
    @Input() readonly label: string;
    @Input() readonly tooltip: string;

    constructor(
        private readonly controlContainer: ControlContainer)
    { }


    get formGroup(): FormGroup
    {
        if (!this.controlContainer.control)
        {
            throw new Error("Cannot access either formGroup or controlName.");
        }
        return this.controlContainer.control as FormGroup;
    }

    get formControl(): FormControl<boolean>
    {
        if (!this.formGroup || (this.controlName !== undefined && this.controlName == null))
        {
            throw new Error("Cannot access either formGroup or controlName.");
        }

        const controlNameStr = typeof this.controlName === "string" ? this.controlName : `${this.controlName}`;
        return this.formGroup.get(controlNameStr) as FormControl<boolean>;
    }

    get lgdFormControl(): LgdFormControl<LgdFormControlBaseConfig, boolean>
    {
        return LgdFormControl.isLgdFormControl(this.formControl) ? this.formControl : undefined;
    }

    get showTooltip(): boolean
    {
        return this.tooltip?.length > 0;
    }

    ngAfterViewInit(): void
    {
        if (!this.inputElement)
        {
            this.log.warning(`ngAfterViewInit - ViewChild inputElement is not defined!`);
        }

        if (this.lgdFormControl)
        {
            this.lgdFormControl.onFocus
                .pipe(takeUntil(this.destructor$))
                .subscribe((shouldFocus: boolean) =>
                {
                    if (shouldFocus)
                    {
                        /**
                         * Calling in a setTimeout to avoid a bug in our current @angular version.
                         */
                        setTimeout(() =>
                        {
                            this.inputElement.nativeElement.focus();
                        });
                    }
                });
        }
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }
}
